<script setup lang="ts">
   import { computed } from 'vue';
   import type { TournamentEntryFieldChoice } from '~/types/types';

   const props = defineProps<{
      field: TournamentEntryFieldChoice;
      modelValue: string | string[] | boolean | undefined;
      error?: string;
      disabled?: boolean;
   }>();

   const emit = defineEmits<{
      'update:modelValue': [value: string | string[] | boolean];
   }>();

   const inputId = computed(() => `te-field-${props.field.name}`);
   const isSingleCheckbox = computed(
      () => props.field.type === 'checkbox' && (!props.field.options || props.field.options.length === 0)
   );

   const onSelectChange = (event: Event) => {
      const target = event.target as HTMLSelectElement;
      if (props.field.type === 'multiselect') {
         const values = Array.from(target.selectedOptions).map((opt) => opt.value);
         emit('update:modelValue', values);
         return;
      }
      emit('update:modelValue', target.value);
   };

   const onRadioChange = (value: string) => {
      emit('update:modelValue', value);
   };

   const onCheckboxToggle = (event: Event, optionValue: string) => {
      const target = event.target as HTMLInputElement;
      const current = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
      if (target.checked) {
         if (!current.includes(optionValue)) current.push(optionValue);
      } else {
         const index = current.indexOf(optionValue);
         if (index !== -1) current.splice(index, 1);
      }
      emit('update:modelValue', current);
   };

   const onSingleCheckboxToggle = (event: Event) => {
      const target = event.target as HTMLInputElement;
      emit('update:modelValue', target.checked);
   };

   const isOptionChecked = (option: string) =>
      Array.isArray(props.modelValue) && props.modelValue.includes(option);

   const baseControlClass =
      'w-full rounded-md border bg-white px-3 py-2 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';
</script>

<template>
   <div class="mb-4 flex flex-col gap-1.5">
      <label v-if="!isSingleCheckbox" :for="inputId" class="text-sm font-semibold text-gray-800">
         {{ field.label }}
         <span v-if="field.required" class="ml-0.5 text-red-700" aria-hidden="true">*</span>
      </label>

      <select
         v-if="field.type === 'select'"
         :id="inputId"
         :value="modelValue as string"
         :required="field.required"
         :disabled="disabled"
         :class="[baseControlClass, error ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500']"
         @change="onSelectChange">
         <option value="" disabled>{{ field.placeholder ?? 'Select an option' }}</option>
         <option v-for="opt in field.options ?? []" :key="opt" :value="opt">{{ opt }}</option>
      </select>

      <select
         v-else-if="field.type === 'multiselect'"
         :id="inputId"
         multiple
         :value="Array.isArray(modelValue) ? modelValue : []"
         :required="field.required"
         :disabled="disabled"
         :class="[baseControlClass, 'min-h-32', error ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500']"
         @change="onSelectChange">
         <option v-for="opt in field.options ?? []" :key="opt" :value="opt">{{ opt }}</option>
      </select>

      <div v-else-if="field.type === 'radio'" class="flex flex-col gap-2">
         <label v-for="opt in field.options ?? []" :key="opt" class="flex cursor-pointer items-center gap-2 text-base text-gray-800">
            <input
               type="radio"
               :name="field.name"
               :value="opt"
               :checked="modelValue === opt"
               :required="field.required"
               :disabled="disabled"
               @change="onRadioChange(opt)" />
            <span>{{ opt }}</span>
         </label>
      </div>

      <div v-else-if="field.type === 'checkbox' && !isSingleCheckbox" class="flex flex-col gap-2">
         <label v-for="opt in field.options ?? []" :key="opt" class="flex cursor-pointer items-center gap-2 text-base text-gray-800">
            <input
               type="checkbox"
               :value="opt"
               :checked="isOptionChecked(opt)"
               :disabled="disabled"
               @change="onCheckboxToggle($event, opt)" />
            <span>{{ opt }}</span>
         </label>
      </div>

      <label v-else-if="isSingleCheckbox" class="flex cursor-pointer items-center gap-2 text-base text-gray-800">
         <input
            :id="inputId"
            type="checkbox"
            :checked="modelValue === true"
            :required="field.required"
            :disabled="disabled"
            @change="onSingleCheckboxToggle" />
         <span>
            {{ field.label }}
            <span v-if="field.required" class="ml-0.5 text-red-700" aria-hidden="true">*</span>
         </span>
      </label>

      <p v-if="error" class="text-xs text-red-700">{{ error }}</p>
   </div>
</template>
